import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import './AgentPerformance.css';

const formatDuration = (seconds) => {
  if (!seconds || seconds < 1) return '0m';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m >= 60 ? `${Math.floor(m/60)}h ${m%60}m` : `${m}m ${s}s`;
};

const formatDate = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatStatus = (s) => String(s || '').replace(/_/g, ' ');

const STATUS_ROWS = [
  { key: 'newLeads',           label: 'New',           cls: 'bar-new'            },
  { key: 'assignedLeads',      label: 'Assigned',      cls: 'bar-assigned'       },
  { key: 'contactedLeads',     label: 'Contacted',     cls: 'bar-contacted'      },
  { key: 'followUpLeads',      label: 'Follow Up',     cls: 'bar-follow_up'      },
  { key: 'notInterestedLeads', label: 'Not Interested', cls: 'bar-not_interested'},
  { key: 'convertedLeads',     label: 'Converted',     cls: 'bar-converted'      },
  { key: 'closedLeads',        label: 'Closed',        cls: 'bar-closed'         },
];

const getStatusBadgeClass = (s) => `badge badge-${String(s||'').toLowerCase().replace(/_/g, '-')}`;

export default function AgentPerformance({ auth }) {
  const [agents, setAgents] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Load agent summary list
  useEffect(() => {
    setLoadingList(true);
    api.get('/api/agent-performance')
      .then(res => setAgents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAgents([]))
      .finally(() => setLoadingList(false));
  }, []);

  // Load agent detail when selected
  const fetchDetail = (agent) => {
    setSelectedAgent(agent);
    setLoadingDetail(true);
    setDetail(null);
    api.get(`/api/agent-performance/${agent.userId}`, { params: { startDate, endDate } })
      .then(res => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  };

  const applyFilter = () => {
    if (selectedAgent) fetchDetail(selectedAgent);
  };

  // Filtered agent list
  const filteredAgents = useMemo(() =>
    agents.filter(a => {
      const matchSearch = search === '' ||
        (a.fullName || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.username || '').toLowerCase().includes(search.toLowerCase());
      const matchRole = statusFilter === 'ALL' || a.role === statusFilter;
      return matchSearch && matchRole;
    }), [agents, search, statusFilter]
  );

  // Summary KPIs from list
  const totalAgents = agents.length;
  const totalConverted = agents.reduce((s, a) => s + (a.convertedLeads || 0), 0);
  const totalCalls = agents.reduce((s, a) => s + (a.totalCalls || 0), 0);
  const totalLeads = agents.reduce((s, a) => s + (a.totalAssignedLeads || 0), 0);

  const maxConverted = useMemo(() =>
    Math.max(...agents.map(a => a.convertedLeads || 0), 1), [agents]);

  const maxDetailStatus = detail
    ? Math.max(...STATUS_ROWS.map(r => detail[r.key] || 0), 1)
    : 1;

  const leadTableFiltered = useMemo(() => {
    if (!detail?.recentLeads) return [];
    return detail.recentLeads;
  }, [detail]);

  return (
    <div className="perf-page">
      {/* Header */}
      <div className="perf-header">
        <div className="perf-header-text">
          <div className="page-eyebrow">Admin & Manager View</div>
          <h1>User & Agent Performance</h1>
          <p>Click any agent to see their full lead breakdown, call stats, and performance report.</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="perf-kpi-strip">
        <div className="perf-kpi-item" style={{'--kpi-bar-color': '#4f46e5'}}>
          <div className="perf-kpi-label">Total Agents</div>
          <div className="perf-kpi-value">{totalAgents}</div>
        </div>
        <div className="perf-kpi-item" style={{'--kpi-bar-color': '#10b981'}}>
          <div className="perf-kpi-label">Total Leads Assigned</div>
          <div className="perf-kpi-value">{totalLeads.toLocaleString()}</div>
        </div>
        <div className="perf-kpi-item" style={{'--kpi-bar-color': '#f59e0b'}}>
          <div className="perf-kpi-label">Total Calls Made</div>
          <div className="perf-kpi-value">{totalCalls.toLocaleString()}</div>
        </div>
        <div className="perf-kpi-item" style={{'--kpi-bar-color': '#f43f5e'}}>
          <div className="perf-kpi-label">Total Conversions</div>
          <div className="perf-kpi-value">{totalConverted.toLocaleString()}</div>
        </div>
      </div>

      <div className="perf-layout">
        {/* LEFT: Agent List Panel */}
        <div className="perf-panel">
          <div className="perf-panel-header">
            <h3>Team Members</h3>
            <span className="perf-panel-count">{filteredAgents.length}</span>
          </div>

          {/* Search & Filter */}
          <div className="perf-search-wrap">
            <input
              type="text"
              placeholder="Search by name or username..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 1rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>
            {['ALL', 'AGENT', 'MANAGER'].map(role => (
              <button
                key={role}
                onClick={() => setStatusFilter(role)}
                style={{
                  padding: '3px 12px', borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-color)',
                  background: statusFilter === role ? 'var(--primary)' : '#fff',
                  color: statusFilter === role ? '#fff' : 'var(--text-muted)',
                  fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >{role}</button>
            ))}
          </div>

          {/* Agent Rows */}
          <div className="perf-agent-list">
            {loadingList ? (
              <div className="perf-loading">
                <svg className="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Loading agents...
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="perf-loading">No agents found.</div>
            ) : (
              filteredAgents.map(agent => (
                <div
                  key={agent.userId}
                  className={`perf-agent-row ${selectedAgent?.userId === agent.userId ? 'selected' : ''}`}
                  onClick={() => fetchDetail(agent)}
                >
                  <div className="perf-agent-avatar">
                    {(agent.fullName?.[0] || agent.username?.[0] || 'A').toUpperCase()}
                  </div>
                  <div className="perf-agent-info">
                    <div className="perf-agent-name">{agent.fullName || agent.username}</div>
                    <div className="perf-agent-handle">
                      @{agent.username}
                      {' · '}
                      <span className={`role-pill role-pill-${(agent.role || '').toLowerCase()}`}>{agent.role}</span>
                    </div>
                  </div>
                  <div className="perf-agent-metric">
                    <strong>{agent.convertedLeads || 0}</strong>
                    <small>converted</small>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Detail Panel */}
        <div className="perf-detail">
          {!selectedAgent ? (
            <div className="perf-empty-state">
              <div className="perf-empty-icon">👥</div>
              <h3>Select an agent to view performance</h3>
              <p>Click any agent from the left panel to view their detailed lead breakdown, call statistics, and activity report.</p>
            </div>
          ) : loadingDetail ? (
            <div className="perf-detail-card">
              <div className="perf-loading">
                <svg className="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Loading agent data...
              </div>
            </div>
          ) : detail ? (
            <>
              {/* Agent Profile Banner */}
              <div className="perf-agent-profile">
                <div className="perf-agent-profile-avatar">
                  {(detail.fullName?.[0] || detail.username?.[0] || 'A').toUpperCase()}
                </div>
                <div className="perf-agent-profile-info">
                  <h2>{detail.fullName || detail.username}</h2>
                  <p>@{detail.username} · {detail.email || 'No email'}</p>
                  <div className="perf-agent-profile-tags">
                    <span className="perf-tag">{detail.role}</span>
                    <span className={`perf-tag ${detail.status === 'ACTIVE' ? 'online' : 'offline'}`}>
                      {detail.status === 'ACTIVE' ? '● ACTIVE' : '○ INACTIVE'}
                    </span>
                    {detail.phone && <span className="perf-tag">📞 {detail.phone}</span>}
                  </div>
                </div>
              </div>

              {/* Date Filter */}
              <div className="perf-detail-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="perf-date-filter">
                  <label>From</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                  <label>To</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                  <button className="perf-filter-btn" onClick={applyFilter}>Apply</button>
                </div>

                {/* Call Stats */}
                <div style={{ padding: '1.25rem' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>📞</span> Call Statistics
                  </h3>
                  <div className="perf-stats-grid">
                    <div className="perf-stat-box">
                      <div className="perf-stat-box-val">{detail.totalCalls}</div>
                      <div className="perf-stat-box-label">Total Calls</div>
                    </div>
                    <div className="perf-stat-box">
                      <div className="perf-stat-box-val">{detail.connectedCalls}</div>
                      <div className="perf-stat-box-label">Connected</div>
                    </div>
                    <div className="perf-stat-box">
                      <div className="perf-stat-box-val">{formatDuration(detail.avgCallDurationSeconds)}</div>
                      <div className="perf-stat-box-label">Avg Duration</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Status Breakdown */}
              <div className="perf-detail-card">
                <h3>
                  <span>📊</span> Lead Pipeline Breakdown
                  <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                    {detail.totalAssignedLeads} total assigned
                  </span>
                </h3>
                <div className="perf-status-rows">
                  {STATUS_ROWS.map(({ key, label, cls }) => (
                    <div key={key} className="perf-status-row">
                      <span className="perf-status-label">{label}</span>
                      <div className="perf-status-bar-wrap">
                        <div
                          className={`perf-status-bar ${cls}`}
                          style={{ width: `${((detail[key] || 0) / maxDetailStatus) * 100}%` }}
                        />
                      </div>
                      <span className="perf-status-count">{detail[key] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Leads Table */}
              <div className="perf-detail-card">
                <h3>
                  <span>📋</span> Assigned Leads
                  <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                    Showing last 50
                  </span>
                </h3>
                {leadTableFiltered.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    No leads assigned to this agent.
                  </div>
                ) : (
                  <div className="perf-lead-table-wrap">
                    <table className="perf-lead-table">
                      <thead>
                        <tr>
                          <th>Lead Name</th>
                          <th>Phone</th>
                          <th>Company</th>
                          <th>Status</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadTableFiltered.map((lead, i) => (
                          <tr key={i}>
                            <td>
                              <Link
                                to={`/leads/${lead.leadId}`}
                                className="perf-lead-link"
                              >
                                {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || `Lead #${lead.leadId}`}
                              </Link>
                            </td>
                            <td>{lead.phone || '—'}</td>
                            <td>{lead.company || '—'}</td>
                            <td>
                              <span className={getStatusBadgeClass(lead.status)}>
                                {formatStatus(lead.status)}
                              </span>
                            </td>
                            <td>{formatDate(lead.updatedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="perf-empty-state">
              <div className="perf-empty-icon">⚠️</div>
              <h3>Failed to load agent data</h3>
              <p>Please try selecting the agent again.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
